import React, { useEffect, useState } from 'react';
import {
  type fuelMeasureType,
  type FuelAnalyticsChartToggleList,
} from '../types/fuelAnalytics.types';
import { Box, Typography } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { Selector } from 'components/Form/Selectors/Selector';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

interface ChartDataTogglerProps {
  selectedDataKey: fuelMeasureType;
  handleChange: (value: fuelMeasureType) => void;
  label?: string;
  hiddenDropDownItems?: fuelMeasureType[];
}
const ChartDataToggler = ({
  selectedDataKey,
  handleChange,
  label,
  hiddenDropDownItems,
}: ChartDataTogglerProps) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const [chartToggleOptions, setChartToggleOptions] =
    useState<FuelAnalyticsChartToggleList>([
      {
        key: 'consumption',
        label: t('fuel_usage_dropdown_consumption'),
      },
      {
        key: 'co2_emission',
        label: t('co2_emission'),
      },
      // {
      //   key: 'cost',
      //   label: 'Cost',
      // },
      {
        key: 'num_of_idle_events',
        label: t('no_of_idle_events'),
      },
      {
        key: 'total_duration_of_idle_events',
        label: t('total_duration_of_idle_events'),
      },
      {
        key: 'loaded_fuel_consumption',
        label: t('loaded_fuel_consumption'),
      },
      {
        key: 'unloaded_fuel_consumption',
        label: t('unloaded_fuel_consumption'),
      },
      {
        key: 'avg_fuel_rate',
        label: t('avg_fuel_rate'),
      },
      {
        key: 'fuel_consumed_per_ton',
        label: t('fuel_consumed_per_ton'),
      },
    ]);

  useEffect(() => {
    if (hiddenDropDownItems) {
      setChartToggleOptions(
        chartToggleOptions.filter(
          (item) => !hiddenDropDownItems.includes(item.key)
        )
      );
    }
  }, [hiddenDropDownItems]);

  return (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}
      minWidth={300}
      maxWidth={500}
    >
      <Box
        style={{
          color: enableDarkTheme ? ThemePalette.typography.white : '#3d3d3d',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Box sx={{ marginLeft: '20px', display: 'flex', alignItems: 'center' }}>
          <Typography style={{ fontSize: '12px', padding: '5px 10px 5px 0' }}>
            {label ?? t('view_chart_by')}
          </Typography>
          <Selector
            minWidth="1px"
            selectSx={{
              fontSize: '12px',
              minWidth: '220px',
              backgroundColor: enableDarkTheme
                ? ThemePalette.dark.cartesianGrid
                : palette.boxBackground,
              color: palette.textPrimary,
              '& .MuiSelect-icon': {
                color: palette.textPrimary,
              },
              '& fieldset': {
                borderColor: palette.textPrimary,
              },
              ...(enableDarkTheme && {
                '&.MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: palette.cardBorderColor, // <------------------ outline-color by default
                  },
                },
              }),
            }}
            value={selectedDataKey}
            onChange={(event) => {
              handleChange(event.target.value as fuelMeasureType);
            }}
            selectorOptions={chartToggleOptions.map((item) => ({
              id: item.key,
              display: item.label,
            }))}
            enableDarkTheme={enableDarkTheme}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ChartDataToggler;
